import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DateFnsAdapter from "@date-io/date-fns";
import DatePicker from "@mui/lab/DatePicker";
import { t } from "i18next";
import { useAppDispatch } from "../store/hooks";
import {
  changeName,
  changePhoneNumber,
  EstimateModel,
  selectEstimate,
  selectEstimationResult,
} from "../store/features/estimateSlice";
import InputLabel from "@mui/material/InputLabel";
import PhoneNumber from "../components/PhoneNumber";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import {
  CallbackModel,
  changeDate,
  changeNotARobot,
  changeTime,
  selectCallback,
} from "../store/features/callbackSlice";
import GoogleRecaptcha from "../components/GoogleRecaptcha";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import FormHelperText from "@mui/material/FormHelperText";
import { isValidNumber } from "libphonenumber-js";
import isEmpty from "lodash.isempty";
import { useNavigate, useSearchParams } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import { CallbackAPIModel } from "../models/apiModels";
import { createCallback } from "../services/callbackService";
import { useSnackbar } from "notistack";
import { Grid } from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";

const dateFns = new DateFnsAdapter();

function validateForm(estimate: EstimateModel, callback: CallbackModel) {
  const errors: any = {};

  if (estimate.name == null || estimate.name.trim() === "") {
    errors.name = { required: true };
  }

  if (
    estimate.phoneNumber?.number == null ||
    estimate.phoneNumber.number.trim() === ""
  ) {
    errors.phone = { required: true };
  } else if (
    !isValidNumber(
      estimate.phoneNumber.number,
      estimate.phoneNumber.prefix?.code
    )
  ) {
    errors.phone = { invalidNumber: true };
  }

  if (!callback.preferredDate) {
    errors.date = { required: true };
  }

  if (!callback.preferredTime) {
    errors.time = { required: true };
  }

  if (!callback.notARobot) {
    errors.notARobot = { required: true };
  }

  return errors;
}

export default function Callback() {
  const estimate = useSelector(selectEstimate);
  const estimateResult = useSelector(selectEstimationResult);
  const callback = useSelector(selectCallback);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { enqueueSnackbar } = useSnackbar();

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [formIsSubmitted, setFormIsSubmmited] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigateBack = useCallback(() => {
    if (searchParams.has("backToEstimate")) {
      navigate("/estimate");
    } else {
      navigate("/");
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    setErrors(validateForm(estimate, callback));
  }, [estimate, callback]);

  const sendCallback = useCallback(async () => {
    setFormIsSubmmited(true);
    if (!isEmpty(errors)) {
      return;
    }

    setIsLoading(true);
    try {
      const selectedTier = estimateResult?.find(
        (e) => e.tier === estimate.tier
      );
      const data = new CallbackAPIModel(callback, estimate, selectedTier);
      await createCallback(data);
      enqueueSnackbar(t("callback.successMessage"), { variant: "success" });
      navigateBack();
    } catch (e) {
      enqueueSnackbar(t("callback.errorMessage"), { variant: "error" });
    }
    setIsLoading(false);
  }, [
    errors,
    estimateResult,
    estimate,
    callback,
    navigateBack,
    enqueueSnackbar,
  ]);

  const selectedDate = useMemo(() => {
    return callback.preferredDate
      ? dateFns.parse(callback.preferredDate, "dd/MM/yyyy")
      : null;
  }, [callback.preferredDate]);

  const onDateSelect = useCallback(
    (date: Date | null) => {
      const dateString = date ? dateFns.formatByString(date, "dd/MM/yyyy") : "";
      dispatch(changeDate(dateString));
    },
    [dispatch]
  );

  const visibleErrors = formIsSubmitted ? errors : {};

  return (
    <Box>
      <Grid container spacing={2} justifyContent={"center"}>
        <Grid item xs={12} sm={5}>
          <Typography variant="h6" fontWeight={"bold"} color={"secondary"}>
            {t("callback.title")}
          </Typography>
          <Typography marginTop={2} variant="subtitle2" fontWeight={"400"}>
            {t("callback.subTitle")}
          </Typography>
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          container
          marginBottom={2}
          rowSpacing={5}
          columnSpacing={2}
        >
          <Grid item xs={12} sm={6}>
            <InputLabel
              error={!!visibleErrors.name}
              htmlFor="name"
              shrink
              required
            >
              {t("start.name")}
            </InputLabel>
            <TextField
              id="name"
              fullWidth
              required
              size="small"
              variant="outlined"
              placeholder={t("start.namePlaceholder")}
              value={estimate.name}
              error={!!visibleErrors.name}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) =>
                dispatch(changeName(ev.currentTarget.value))
              }
            />
            {visibleErrors.name?.required && (
              <FormHelperText error>
                {t("start.validations.requiredField")}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <PhoneNumber
              disabled={false}
              errors={visibleErrors.phone}
              phone={estimate.phoneNumber}
              label={t("start.phoneNumber")}
              placeholder={t("start.phoneNumberPlaceholder")}
              updatePhone={(value) => dispatch(changePhoneNumber(value))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputLabel
              error={!!visibleErrors.name}
              htmlFor="date-picker"
              required
              shrink
            >
              {t("callback.preferredDate")}
            </InputLabel>
            <DatePicker
              inputFormat="dd/MM/yyyy"
              open={datePickerOpen}
              onOpen={() => setDatePickerOpen(true)}
              onClose={() => setDatePickerOpen(false)}
              disablePast={true}
              OpenPickerButtonProps={{ style: { margin: "-0.5rem" } }}
              value={selectedDate}
              onChange={onDateSelect}
              renderInput={(params: TextFieldProps) => (
                <TextField
                  {...params}
                  id="date-picker"
                  variant="outlined"
                  fullWidth
                  required
                  placeholder="DD/MM/YYYY"
                  size="small"
                  error={visibleErrors.date != null}
                  helperText={
                    visibleErrors.date
                      ? t("callback.validations.requiredField")
                      : ""
                  }
                  onClick={(e) => setDatePickerOpen(true)}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputLabel
              error={!!visibleErrors.time}
              htmlFor="name"
              shrink
              required
            >
              {t("callback.preferredTime")}
            </InputLabel>
            <Select
              value={callback.preferredTime || "-1"}
              placeholder="9am - 4pm"
              error={!!visibleErrors.time}
              variant="outlined"
              fullWidth
              size="small"
              required
              SelectDisplayProps={{
                style: {
                  color: callback.preferredTime ? undefined : "#bbb",
                },
              }}
              onChange={(event) => dispatch(changeTime(event.target.value))}
            >
              <MenuItem value="-1" disabled>
                Please Select
              </MenuItem>
              <MenuItem value="7am - 9am">7am - 9am</MenuItem>
              <MenuItem value="9am - 4pm">9am - 4pm</MenuItem>
              <MenuItem value="4pm - 7pm">4pm - 7pm</MenuItem>
            </Select>
            {!!visibleErrors.time && (
              <FormHelperText error>
                {t("start.validations.requiredField")}
              </FormHelperText>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <GoogleRecaptcha
              notARobot={callback.notARobot}
              onUpdateToken={(newToken) => dispatch(changeNotARobot(newToken))}
              action="callbackConfirm"
            />
            {visibleErrors.notARobot?.required && (
              <FormHelperText error>
                {t("start.validations.requiredField")}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={sendCallback}
              disabled={isLoading}
              className="rounded-button"
            >
              <Typography fontWeight={"bold"} marginRight={1}>
                {t("callback.bookNowAction")}
              </Typography>
              {isLoading ? (
                <CircularProgress
                  size={20}
                  style={{ marginLeft: 5 }}
                  color="secondary"
                />
              ) : (
                <ArrowForwardIcon />
              )}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
